<script setup>
import { ref, computed, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import { getAmountButtons } from '@/i18n/helper'
import { apiEndpoints } from '@/config/api'
import { executeApiCall } from '@/lib/error-handler'

const { locale, t } = useI18n()
const { proxy } = getCurrentInstance();
const swal = proxy.$swal

const pointBalance = computed(() => proxy.$store.state.user.point)
const password = ref('')

const usdtExchangeRate = computed(() =>
  proxy.$store.state.settings.usdt_exchange_rate
    ? proxy.$store.state.settings.usdt_exchange_rate
    : null
)
const amount = ref('')
const quickAmounts = getAmountButtons(locale.value, usdtExchangeRate.value)

const isValid = computed(() => {
  const numAmount = Number(amount.value.replace(/,/g, ''))
  return numAmount > 0 && 
    numAmount <= pointBalance.value && 
    password.value.length >= 6
})

const formatNumber = (value) => {
  return new Intl.NumberFormat().format(value)
}

const handleAmountInput = (event) => {
  let value = event.target.value.replace(/\D/g, '')
  if (value) {
    value = Number(value)
    if (value > pointBalance.value) {
      value = pointBalance.value
    }
    amount.value = formatNumber(value)
  } else {
    amount.value = ''
  }
}

const setQuickAmount = (value) => {
  amount.value = formatNumber(value)
}

const handleSubmit = async () => {
  const numAmount = Number(amount.value.replace(/,/g, ''))

  if (!numAmount || numAmount <= 0) {
    return swal.error({ title: t('point.invalidAmount')})
  }

  const data = await executeApiCall(
    () => proxy.axios.post(apiEndpoints.api_pointToChip, {
      amount: numAmount,
      password: password.value
    })
  )

  const { success, chip, point } = data
  if (success) {
    swal.success(t('point.convertSuccess'))
    pointBalance.value = point
    amount.value = ''
    password.value = ''
    proxy.$store.dispatch('setUser', { user: { chip, point }})
  }
}
</script>

<template>
  <div class="point-to-chip">
    <div class="form-group">
      <label>{{ t('point.currentBalance') }}</label>
      <input type="text" :value="formatNumber(pointBalance)" readonly />
    </div>

    <div class="form-group">
      <label>{{ t('point.amount') }}</label>
      <input 
        type="text" 
        v-model="amount"
        @input="handleAmountInput"
        :placeholder="t('point.enterAmount')"
      />
    </div>

    <div class="quick-amounts">
      <button 
        v-for="value in quickAmounts" 
        :key="value.id"
        @click="setQuickAmount(value.amount)"
        class="quick-amount-btn"
      >
        {{ value.label }}
      </button>
    </div>

    <div class="form-group">
      <label>{{ t('point.password') }}</label>
      <input 
        type="password" 
        v-model="password"
        :placeholder="t('point.enterPassword')"
      />
    </div>

    <button 
      class="submit-btn btn-primary"
      :disabled="!isValid"
      @click="handleSubmit"
    >
      {{ t('point.convert') }}
    </button>
  </div>
</template>

<style scoped>
.point-to-chip {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #ffffff;
  transition: all 0.2s ease;
  color: #333333;
  font-size: 14px;
}

.form-group input::placeholder {
  color: #999999;
}

.form-group input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  background-color: #f8f9fa;
}

.quick-amounts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin: 1rem 0;
}

.quick-amount-btn {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s;
}

.quick-amount-btn:hover {
  background: #e9e9e9;
}

.submit-btn {
  width: 100%;
  padding: 0.75rem;
  border: none;
  border-radius: 4px;
  background: #007bff;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-btn:disabled {
  background: #ccc !important;
  cursor: not-allowed;
}

.submit-btn:not(:disabled):hover {
  background: #0056b3;
}
</style>
